import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Author = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="author"
      ref={ref}
      data-testid="author-section"
      className="relative overflow-hidden bg-white py-20 sm:py-28"
    >
      <div className="pointer-events-none absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#20C9C3]/8 blur-[110px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-16">
          <motion.div
            style={{ y: photoY }}
            className="lg:col-span-5 flex justify-center lg:justify-start"
          >
            <motion.div
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className="relative"
            >
              <div className="absolute -top-5 -left-5 h-full w-full rounded-[20px] border-2 border-[#20C9C3]" />
              <div className="absolute -bottom-5 -right-5 h-24 w-24 border-b-[3px] border-r-[3px] border-[#C5A253]" />
              <img
                src="/images/joede-brown.png"
                alt="Joede Brown, MSA — author of 50 Bookkeeping Red Flags Every Business Owner Should Know"
                data-testid="author-photo"
                className="relative w-[300px] sm:w-[360px] lg:w-full max-w-[400px] rounded-[16px] object-cover shadow-[0_35px_70px_-25px_rgba(30,42,43,0.35)]"
              />
              <div className="absolute -bottom-7 left-6 rounded-full bg-[#1E2A2B] px-6 py-3 shadow-lg">
                <span className="font-display text-sm italic text-white">
                  Joede Brown, <span className="not-italic text-[#20C9C3] font-semibold">MSA</span>
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, delay: 0.12, ease: EASE }}
            className="lg:col-span-7 lg:pl-6"
          >
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-[#C5A253]" />
              <span className="text-[12px] font-bold uppercase tracking-[0.24em] text-[#14A8A3]">
                Meet the Author
              </span>
            </div>
            <h2
              data-testid="author-name"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1E2A2B]"
            >
              Joede Brown, <span className="italic text-[#14A8A3]">MSA</span>
            </h2>
            <p
              data-testid="author-credentials"
              className="mt-4 text-[13px] sm:text-sm font-bold uppercase tracking-[0.16em] text-[#C5A253]"
            >
              Bookkeeper <span className="mx-1 text-[#20C9C3]">•</span> Tax
              Preparer <span className="mx-1 text-[#20C9C3]">•</span> Owner of
              Brown &amp; Associates, LLC
            </p>
            <div className="mt-7 space-y-5 max-w-2xl text-base md:text-lg leading-relaxed text-[#1E2A2B]/70">
              <p>
                Joede Brown is the owner of Brown &amp; Associates, LLC and
                holds a Master of Science in Accounting.
              </p>
              <p>
                Through her work with business owners, she has seen how easily
                bookkeeping problems can sit unnoticed when someone does not
                know what to look for.
              </p>
              <p>
                She wrote{" "}
                <span className="font-display italic text-[#1E2A2B]">
                  50 Bookkeeping Red Flags Every Business Owner Should Know
                </span>{" "}
                to give business owners a practical resource they can use to
                recognize warning signs, ask better questions and know when
                their books need attention.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
